"use client";

import * as React from "react";
import Image from "next/image";
import { ChevronDown } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { ASSET_PREFIX } from "../../../config";

export type AccordionItem = {
  id: string | number;
  title: React.ReactNode;
  content: React.ReactNode;
  disabled?: boolean;
};

export type AccordionProps = {
  items: AccordionItem[];
  single?: boolean;
  defaultOpenIds?: Array<AccordionItem["id"]>;
  className?: string;
  itemClassName?: string;
};

export const Accordion: React.FC<AccordionProps> = ({
  items,
  single = false,
  defaultOpenIds = [],
  className = "",
  itemClassName = "",
}) => {
  const [open, setOpen] = React.useState<Set<AccordionItem["id"]>>(
    () => new Set(defaultOpenIds)
  );

  const toggle = (id: AccordionItem["id"], disabled?: boolean) => {
    if (disabled) return;
    setOpen((prev) => {
      const next = new Set(prev);
      const isOpen = next.has(id);
      if (single) {
        next.clear();
        if (!isOpen) next.add(id);
      } else {
        isOpen ? next.delete(id) : next.add(id);
      }
      return next;
    });
  };

  return (
    <div
      className={
        "w-full divide-y divide-[#b08d38]  border-b border-[#b08d38] bg-white  " +
        className
      }>
      {items.map(({ id, title, content, disabled }, idx) => {
        const isOpen = open.has(id);
        const panelId = `accordion-panel-${id}`;
        const headingId = `accordion-heading-${id}`;
        return (
          <div
            key={id}
            className={
              "group/acc pr-4 sm:pr-6 " +
              (idx === 0 ? "rounded-t-2xl " : "") +
              (idx === items.length - 1 ? "rounded-b-2xl " : "") +
              itemClassName
            }>
            <button
              id={headingId}
              className={
                "flex w-full items-center justify-between gap-4 py-4 text-left outline-none transition-opacity cursor-pointer " +
                (disabled
                  ? "cursor-not-allowed opacity-50"
                  : "hover:opacity-90")
              }
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() => toggle(id, disabled)}
              disabled={disabled}>
              <h2 className="text-[20px] leading-[28px] 2xl:text-[22px] 2xl:leading-[30px] font-medium text-gray-900">
                {title}
              </h2>
              <div className="p-[10px] rounded-[50%] bg-[#b1b1b1] w-[40px] h-[40px] flex items-center justify-center transition-transform duration-300  ">
                <ChevronDown
                  className={
                    "h-6 w-6 shrink-0 text-white  transition-transform duration-300 text-[#000]" +
                    (isOpen ? "rotate-180" : "rotate-0")
                  }
                  aria-hidden="true"
                />
              </div>
            </button>

            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.section
                  key="content"
                  id={panelId}
                  role="region"
                  aria-labelledby={headingId}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.24, ease: [0.2, 0.8, 0.2, 1] }}
                  style={{ overflow: "hidden" }}>
                  <div className="pb-5 text-[16px] leading-[22px] sm:text-base leading-relaxed w-[100%] md:w-[90%]  text-gray-700">
                    {content}
                  </div>
                </motion.section>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};

export const AccordionDemo: React.FC = () => {
  const items: AccordionItem[] = [
    {
      id: 1,
      title: "Advantages of the Location",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            TVS Nagar sets up quick access to Saibaba Colony in just five minutes while also serving as a connector between Thadagam and Mettupalayam Road. This provides residents the convenience of being close to major city landmarks while still enjoying the peace and charm of a quiet suburban neighborhood.

          </p>

        </>
      ),
    },
    {
      id: 2,
      title: "Quite a space, more comfortable",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            Unlike many crowded and noisy neighborhoods, TVS Nagar still has ample open land and relatively low population density. This allows developers to create better-planned projects with freedom, and residents are surrounded by more green and open lands
          </p>

        </>
      ),
    },
    {
      id: 3,
      title: "Infrastructure & Lifestyle Anchors",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            Hospitals such as Sree Abishek and JM Hospital, educational institutions like Vel International Public School, and everyday amenities-including ATMs and local markets-are already in place. TVS Nagar gradually grows into a lifestyle that reflects before it even gets complete development
          </p>

        </>
      ),
    },
    {
      id: 4,
      title: "Affordable living  factor",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            Property rates in TVS Nagar are still more affordable than in well-known areas like RS Puram or Saibaba Colony. Experts say this price difference, along with excellent connectivity, could lead to a sharp rise in value in the near future.
          </p>

        </>
      ),
    },
    {
      id: 5,
      title: "Resident Perspective",
      content: (
        <>
          <p className="text-[16px] le  ding-[24px] mb-[15px] text-[#171717]">
            Locals describe the area as breezy, peaceful, and less congested, with proximity to the greener stretches of Thadagam. “It’s one of those places where you get city convenience and a calmer living environment at the same time,” said one resident.
          </p>

          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            As development picks up pace, TVS Nagar is emerging as the go-to neighborhood for modern living and turning into a residential favorite in Coimbatore. turn into a residential favorite.
          </p>
        </>
      ),
    },
  ];

  return (
    <div className="">
      <Accordion items={items} single defaultOpenIds={[1]} />
    </div>
  );
};

export default function TvsNagar() {
  const data = [
    {
      image: `/images/blogs/tvs-nagar-is-next-to-bloom-as-a-new-residential-hub-for-coimbatore.webp`,
      title: "TVS Nagar is next to bloom as a new residential hub for  Coimbatore - +Willa",
      date: "1 Nov 2025",

      content1: (
        <>
          <p className="text-[16px] leading-[24px] mb-[24px] text-[#171717]">
            once a quiet stretch along Thadagam Road, is now buzzing with potential-fast becoming one of Coimbatore’s most talked-about places to live.

          </p>

          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            Real estate experts say TVS Nagar will be an attractive location for homebuyers because of its lower population density, better roads, and facilities

          </p>

          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            Urban planners and local real estate watchers point to a combination of strategic location, lower density, and improving infrastructure as reasons for its growing appeal.

          </p>

        </>
      ),

    },
  ];

  return (
    <div className="sticky top-[130px] z-[2] ">
      {data.map((item, index) => (
        <div className="group relative" key={index}>
          <h1 className="text-[28px]/[36px] md:text-[32px]/[40px] lg:text-[36px]/[44px] xl:text-[40px]/[48px] 2xl:text-[48px]/[54px] tracking-[-0.03em] font-[500] text-[var(--secondary)]">
            {item.title}
          </h1>
          <p className="text-[#924000] my-[20px] mb-[50px] max-mlg:mb-[30px] text-[20px] leading-[26px]  max-xl:my-[15px] ">
            {item.date}
          </p>

          <div className="rounded-[10px] overflow-hidden">
            <Image
              src={`${ASSET_PREFIX}${item.image}`}
              width={530}
              height={500}
              alt="Blog list"
              unoptimized
              className="w-full max-h-[500px] object-cover group-hover:scale-[1.1] transition duration-300 ease-linear"
            />
          </div>

          <div className="mt-[50px] ">
            <p className="my-[20px] text-[20px] leading-[28px] font-light max-xxl:text-[18px] max-xxl:leading-[26px] group-hover:text-[#8e722d] transition-colors duration-300 max-mlg:text-[16px] max-mlg:leading-[24px] max-xl:my-[15px]">
              {item.title}
            </p>

            <div className="text-[16px] leading-[24px] mb-[20px] w-full font-light">
              {item.content1}
            </div>
          </div>

          <div className="mb-[20px]">
            <AccordionDemo />
          </div>

        </div>
      ))}
    </div>
  );
}
